import i18n from 'i18next';
import { Fragment, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CalendarDays, ChevronDown, CircleAlert, Clock, Search, X } from 'lucide-react';
import { Documento, EXEMPLO_DOCUMENTO, ehTipoDocumentoPais, normalizarDocumentoPais, paisDoTipo, TAMANHO_DOCUMENTO, validarDocumentoPais, type TipoDocumentoPais } from '@malgoos/shared';
import { ErroRequisicao, get, mensagemDeErro } from '../nucleo/api';
import { AtalhoCamposDaTela, useCamposDaTela } from '../nucleo/campos-tela';
import { dentroDoIntervalo, estadoDoTexto, mascarar, textoDoValor, valorDoTexto, type IdiomaCampo, type TipoData } from '../nucleo/campo-data';
import { OCULTO, discretoAtivo, nomesOcultos } from '../nucleo/discreto';
import { chavePix, data, deCentavos, dinheiro, documentoFormatado, email, nomePessoa, numero, numeroConta, telefoneFormatado, textoLivre } from '../nucleo/formato';
import { EXEMPLOS_MASCARA, centavosDoTexto, mascaraCepBR, mascaraDinheiro, mascaraDocumentoDo, mascaraPercentual, mascaraTelefoneBR, percentualDoTexto, textoDoPercentual, type FormatoMascara, type LadoCursor } from '../nucleo/mascaras';
import { useMascaraDigitando } from './cursor-mascara';
import { rotuloCampo } from '../nucleo/rotulos';
import { ehDocumentoPorNome, ehOcultoPorNome, sensivelDoExtra, sensivelPorNome, type DefExtraSensivel, type TipoSensivel } from '../nucleo/sensivel-por-nome';
import { useDiscreto } from './sensivel';
import { TextoCortado } from './texto-cortado';

export type TipoCampo =
  | 'texto'
  | 'email'
  | 'telefone'
  | 'senha'
  | 'numero'
  | 'dinheiro'
  | 'data'
  | 'datahora'
  | 'hora'
  | 'selecao'
  | 'area'
  | 'booleano'
  | 'busca'
  | 'tags'
  | 'cor'
  /** Fase 2 (W5): CPF ou CNPJ com máscara enquanto digita e dica de validade (verde ou vermelha). */
  | 'documento'
  /** Fase 2 (W5): CEP com máscara 00000-000. */
  | 'cep'
  /** Lista B de 30/10 (item 4): percentual com máscara 0,00 % (valor em número, 12,5 para 12,50 %); `min` e `max` limitam o que se digita. */
  | 'percentual';

export interface Opcao {
  valor: string;
  rotulo: string;
  /** Opção que aparece mas não se escolhe (o motivo vai na ajuda do campo). */
  desabilitado?: boolean;
}

export interface DefCampo {
  nome: string;
  rotulo: string;
  tipo: TipoCampo;
  obrigatorio?: boolean;
  opcoes?: Opcao[];
  /** Seleção remota: GET no caminho com `busca`, lendo `itens`. */
  busca?: { caminho: string; params?: Record<string, unknown>; rotulo: (item: Record<string, unknown>) => string; valor?: (item: Record<string, unknown>) => string; /** Review r63 (VIS-M1): o rótulo é nome de pessoa e passa pelo `nomePessoa` (modo discreto) mesmo fora dos caminhos de nome. */ pessoa?: boolean };
  largura?: 'inteira' | 'meia' | 'terco';
  ajuda?: string;
  placeholder?: string;
  min?: number;
  max?: number;
  passo?: number;
  desabilitado?: boolean;
  /** Mostra o campo só quando a condição é verdadeira. */
  visivel?: (valores: Record<string, unknown>) => boolean;
  /**
   * Lote de 19/10 (item 9): campo configurável da tela (Configurações > Campos das telas) quando o nome não é a chave nem
   * um subcampo dela (`desconto_tipo` é do campo `desconto`). Sem ele, vale o próprio nome (`endereco.cep` é de `endereco`).
   */
  campoTela?: string;
  /**
   * Máscara do modo discreto quando o nome do campo não basta (`numero` da casa ou do RG, nome de pessoa). Sem ela, o campo é
   * reconhecido pelo nome: documento, CPF, CNPJ, partes do endereço, agência, conta, Pix e nascimento.
   */
  sensivel?: MascaraCampo;
  /**
   * Campo personalizado (`extras.<chave>`): a marca "sensível" da definição (review r15 spec M1). Com ela, texto e lista somem no
   * modo discreto mesmo sem nome sensível; número e data somem sempre (`sensivelDoExtra`).
   */
  sensivelNaDefinicao?: boolean;
  /**
   * Nomes conhecidos que o texto livre do campo pode citar (o parceiro no título da oportunidade, r10 visual M2): com o modo
   * discreto e a opção de nomes, viram iniciais no campo sem foco, como no cabeçalho e no cartão.
   */
  nomes?: (string | null | undefined)[];
  /**
   * Fase 2 (W5): documento esperado no tipo `documento` (CPF, CNPJ ou qualquer um dos dois, pelo tamanho). Lote 19/10: também
   * os de outros países (EIN, SSN e ITIN dos EUA; CIF, NIF e NIE da Espanha), com a máscara e o controle do tipo.
   */
  documento?: 'cpf' | 'cnpj' | 'qualquer' | Exclude<TipoDocumentoPais, 'cpf' | 'cnpj'>;
  /** Fase 2 (W5): tipo original do campo personalizado (telefone, documento, email, cep, dinheiro, texto_longo...). */
  tipoExtra?: string;
  /**
   * Fase 2 (W5): consulta que preenche outros campos quando este fica completo e válido (CNPJ com 14 dígitos, CEP com 8). O
   * `Formulario` só preenche os campos que estão VAZIOS (nunca sobrescreve o que foi digitado) e não repete a mesma consulta.
   */
  preencher?: (valor: string) => Promise<Record<string, unknown> | null>;
  /**
   * RCRM5 SPEC-B3 e ROB-B4: o `preencher` é uma sugestão que segue o valor deste campo (o regime da empresa escolhida). Trocar
   * o valor troca o que a consulta anterior preencheu, enquanto a pessoa não mudou à mão; com o campo já preenchido na
   * abertura, a consulta roda no início. Sem a opção, a consulta só preenche os vazios (W5).
   */
  preencherSugere?: boolean;
  /** Fase 2 (W5): título de grupo (o mesmo kicker de "Campos personalizados") antes do primeiro campo visível do grupo. */
  grupo?: string;
  /** Fase 2 (W19): campos que voltam a vazio quando este muda (a chave Pix ao trocar o tipo da chave). */
  limpa?: string[];
  /** Texto fixo visível dentro do input, antes do valor (o "@" do usuário do WhatsApp); não faz parte do valor gravado. */
  prefixo?: string;
  /** Lista de 02/11 (CRM, item 1): controle à esquerda do campo, na mesma linha (o tipo do telefone, que fica fora dos valores do formulário). O rótulo segue ligado ao campo. */
  ladoEsquerdo?: ReactNode;
  /** Fase 2 (W19): máscara aplicada ao texto enquanto digita (telefone, chave aleatória do Pix). */
  transformar?: (valor: string) => string;
  /** Fase 2 (W19): validação local além de obrigatório e e-mail; devolve a mensagem do erro ou null. Campo vazio não é conferido. */
  validar?: (valor: unknown) => string | null;
}

/** Fase 2 (W5): máscara de CPF (000.000.000-00) ou CNPJ (00.000.000/0000-00) enquanto digita; `qualquer` escolhe pelo tamanho. As máscaras vivem em `nucleo/mascaras.ts` (item 4 da lista B de 30/10). */
export function mascaraDocumento(v: string, qual: NonNullable<DefCampo['documento']> = 'qualquer'): string {
  return mascaraDocumentoDo(v, qual);
}

/** Fase 2 (W5): máscara de CEP (00000-000). */
export function mascaraCep(v: string): string {
  return mascaraCepBR(v);
}

export function validadeDocumento(v: string, qual: NonNullable<DefCampo['documento']> = 'qualquer'): 'cpfValido' | 'cpfInvalido' | 'cnpjValido' | 'cnpjInvalido' | null {
  if (qual !== 'cpf' && qual !== 'cnpj' && qual !== 'qualquer') return null;
  const d = v.replace(/\D/g, '');
  const tipo = d.length === 11 && qual !== 'cnpj' ? 'cpf' : d.length === 14 && qual !== 'cpf' ? 'cnpj' : null;
  if (!tipo) return null;
  const r = Documento.tryCreate(d);
  return r.ok && r.valor.tipo === tipo ? (`${tipo}Valido` as const) : (`${tipo}Invalido` as const);
}

/**
 * Lote 19/10: validade do documento de outro país (EIN, SSN, ITIN, CIF, NIF, NIE) quando completo; null enquanto incompleto
 * ou para CPF e CNPJ (esses pela `validadeDocumento`).
 */
export function validadeDocumentoPais(v: string, tipo: DefCampo['documento']): 'valido' | 'invalido' | null {
  if (!ehTipoDocumentoPais(tipo) || tipo === 'cpf' || tipo === 'cnpj') return null;
  if (normalizarDocumentoPais(tipo, v).length < TAMANHO_DOCUMENTO[tipo]) return null;
  return validarDocumentoPais(tipo, v).ok ? 'valido' : 'invalido';
}

/** Valor completo para a consulta do campo (`preencher`): CNPJ válido com 14 dígitos, CEP com 8 ou o id da busca; null se não estiver pronto. */
export function prontoParaConsulta(def: Pick<DefCampo, 'tipo'>, v: unknown): string | null {
  const d = String(v ?? '').replace(/\D/g, '');
  if (def.tipo === 'cep') return d.length === 8 ? d : null;
  if (def.tipo === 'documento') return validadeDocumento(d, 'cnpj') === 'cnpjValido' ? d : null;
  // Lote crm5 (E10): a busca escolhida (o id do registro) também pode preencher outros campos vazios (o regime da empresa).
  if (def.tipo === 'busca') return typeof v === 'string' && v.trim() ? v : null;
  return null;
}

const vazioNoForm = (v: unknown) => v === undefined || v === null || (typeof v === 'string' && !v.trim()) || (Array.isArray(v) && !v.length);

/** Mescla o que a consulta trouxe SÓ nos campos vazios (fase 2, W5: a consulta nunca sobrescreve o que foi digitado). */
export function mesclarVazios(valores: Record<string, unknown>, dados: Record<string, unknown>): Record<string, unknown> {
  let r = valores;
  for (const [k, v] of Object.entries(dados)) if (!vazioNoForm(v) && vazioNoForm(ler(r, k))) r = escrever(r, k, v);
  return r;
}

/**
 * RCRM5 SPEC-B3 e ROB-B4: mescla a resposta de uma consulta que sugere (`preencherSugere`). Cada campo vazio, ou ainda com o
 * valor que a consulta anterior pôs (`antes`), recebe o novo; o que a pessoa mudou à mão fica. O que a consulta anterior pôs e
 * a nova não traz volta a vazio. Devolve os valores e o que esta consulta preencheu (para a próxima troca).
 */
export function mesclarSugestao(
  valores: Record<string, unknown>,
  dados: Record<string, unknown> | null,
  antes: Record<string, unknown>,
): { valores: Record<string, unknown>; preenchidos: Record<string, unknown> } {
  let r = valores;
  const preenchidos: Record<string, unknown> = {};
  const intocado = (k: string) => {
    const atual = ler(r, k);
    return vazioNoForm(atual) || (k in antes && JSON.stringify(atual) === JSON.stringify(antes[k]));
  };
  for (const k of Object.keys(antes)) if (!(dados && k in dados && !vazioNoForm(dados[k])) && intocado(k) && !vazioNoForm(ler(r, k))) r = escrever(r, k, null);
  for (const [k, v] of Object.entries(dados ?? {})) {
    if (vazioNoForm(v) || !intocado(k)) continue;
    r = escrever(r, k, v);
    preenchidos[k] = v;
  }
  return { valores: r, preenchidos };
}

/**
 * Lote 19 (design system da Malgo): no celular o campo tem 44px (alvo de toque do BrandBook) e texto de 16px (o navegador do
 * celular não dá zoom ao focar); a partir de 768px, 40px e 14px como antes. Fundo do campo pelo token `--campo`.
 */
const classeEntrada =
  'h-11 w-full min-w-0 rounded-lg border border-borda-campo bg-campo px-3 text-base text-tinta-1 placeholder:text-tinta-3 focus:border-foco focus:outline-2 focus:outline-offset-0 focus:outline-foco disabled:opacity-60 md:h-10 md:text-sm';

export function Rotulo({ id, rotulo, obrigatorio, erro, ajuda, children }: { id: string; rotulo: string; obrigatorio?: boolean; erro?: string; ajuda?: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-tinta-2">
        {rotulo}
        {obrigatorio && <span className="ml-0.5 text-acento-forte">*</span>}
      </label>
      {children}
      {erro ? (
        // Lote 19 (BrandBook, "informação além da cor"): o erro leva o ícone de alerta junto da cor.
        <p id={`${id}-erro`} className="flex items-start gap-1 text-xs text-perigo">
          <CircleAlert className="mt-px size-3.5 shrink-0" aria-hidden />
          <span className="min-w-0">{erro}</span>
        </p>
      ) : (
        ajuda && (
          <p id={`${id}-ajuda`} className="text-xs text-tinta-3">
            {ajuda}
          </p>
        )
      )}
    </div>
  );
}

export function Entrada(props: React.InputHTMLAttributes<HTMLInputElement> & { ref?: React.Ref<HTMLInputElement> }) {
  return <input {...props} className={`${classeEntrada} ${props.className ?? ''}`} />;
}

/** Máscara do modo discreto por tipo de campo sensível. `nome` segue a opção de nomes (iniciais); as outras, o modo. */
export type MascaraCampo = 'dinheiro' | 'documento' | 'telefone' | 'email' | 'oculto' | 'conta' | 'pix' | 'nome';

/** Campo de documento pelo nome (CPF, CNPJ, RG, PIS, título, passaporte...): a regra única de `nucleo/sensivel-por-nome.ts`. */
export const campoDeDocumento = (nome: string) => ehDocumentoPorNome(sensivelPorNome(nome));
/** Campo de data que o modo discreto esconde (nascimento, em qualquer posição: `nascimento_socio`). */
export const campoDeNascimento = (nome: string) => sensivelPorNome(nome) === 'nascimento';

/** Máscara do Editar pelo tipo sensível que o nome indica (review r14: a mesma regra da ficha, da Tabela e da Auditoria). */
function mascaraDoTipoSensivel(tipo: TipoSensivel | null): MascaraCampo | null {
  if (!tipo) return null;
  if (ehDocumentoPorNome(tipo)) return 'documento';
  if (tipo === 'conta') return 'conta';
  if (tipo === 'pix') return 'pix';
  if (tipo === 'email') return 'email';
  if (tipo === 'telefone') return 'telefone';
  if (tipo === 'nome') return 'nome';
  return 'oculto';
}

/** Campo personalizado: o nome do campo no formulário é `extras.<chave>`. */
const ehExtra = (nome: string) => nome.startsWith('extras.');

/** A definição de um campo personalizado no formato da regra efetiva (`sensivelDoExtra`). */
const defDoExtra = (def: Pick<DefCampo, 'tipo'> & Partial<Pick<DefCampo, 'rotulo' | 'sensivelNaDefinicao' | 'tipoExtra'>>): DefExtraSensivel => ({ tipo: def.tipoExtra ?? def.tipo, rotulo: def.rotulo, sensivel: def.sensivelNaDefinicao });

type DefMascara = Pick<DefCampo, 'nome' | 'tipo' | 'sensivel'> & Partial<Pick<DefCampo, 'rotulo' | 'sensivelNaDefinicao' | 'tipoExtra'>>;

/**
 * Máscara de um campo pelo nome (r9 spec B4; review r14 visual M1 e M2, spec M2): `sensivelPorNome` (termo em qualquer
 * posição: `rg`, `pis`, `pix`, `conta_corrente`, `cep_entrega`, `valor_contrato`...), a MESMA regra da ficha e da Tabela
 * (`valorExtra`) e da Auditoria (`censura-json`). Vale para texto e número; data sensível vai pelo `CampoData sensivel`.
 * Review r15 spec M1: campo personalizado (`extras.*`) segue a regra efetiva `sensivelDoExtra` (número e data sempre; texto e
 * lista pela chave, pelo rótulo ou pela marca da definição; sim ou não nunca), também na lista (`SelecaoSensivel`).
 */
export function mascaraDoCampo(def: DefMascara): MascaraCampo | null {
  if (def.sensivel) return def.sensivel;
  if (ehExtra(def.nome)) {
    const tipo = sensivelDoExtra(def.nome, defDoExtra(def));
    return tipo === 'oculto' ? 'oculto' : mascaraDoTipoSensivel(tipo);
  }
  if (def.tipo === 'email') return 'email';
  if (def.tipo === 'telefone') return 'telefone';
  if (def.tipo !== 'texto' && def.tipo !== 'numero') return null;
  return mascaraDoTipoSensivel(sensivelPorNome(def.nome));
}

/**
 * O campo está mascarado sem foco no modo discreto? Máscara do tipo (documento, e-mail...) com o modo (ou, em `nome`, com a
 * opção de nomes) ou, em texto livre (`livre`), quando o texto tem padrão reconhecível ou nome conhecido (`textoLivre`, r10
 * visual M2 e B5). Campo vazio ou sem nada a esconder não é mascarado (e pode receber o foco inicial).
 */
export function campoMascarado(real: string, mascara: MascaraCampo | null, livre: boolean, nomesNoTexto: (string | null | undefined)[], ativo: boolean, nomes: boolean): boolean {
  if (!real) return false;
  if (mascara && mascara !== 'nome') return ativo;
  // Nome de pessoa: com a opção de nomes, sempre; sem ela, o texto ainda passa pela censura do texto livre (review r14: o campo
  // `solicitante_nome` com "pagar R$ 7.654,32" ficava cru sem a opção de nomes).
  if (mascara === 'nome' && nomes) return true;
  if (!livre || !ativo) return false;
  return textoLivre(real, ...nomesNoTexto) !== real;
}

/** `aria-invalid` ligado (campo com erro de validação nunca vira marcador: "•••••" com o erro embaixo não dá para corrigir). */
const invalido = (v: unknown) => v === true || v === 'true' || v === 'grammar' || v === 'spelling';

/** Foca o campo real e põe o cursor no fim (e-mail não tem seleção: o foco basta). */
function focarNoFim(el: HTMLInputElement | HTMLTextAreaElement | null) {
  if (!el) return;
  el.focus();
  try {
    el.setSelectionRange(el.value.length, el.value.length);
  } catch {
    /* tipo sem seleção (email) */
  }
  if (el instanceof HTMLTextAreaElement) el.scrollTop = el.scrollHeight;
}

/** Texto visível de um rótulo, sem a marca de obrigatório e sem o próprio marcador. */
function textoDoRotulo(label: Element | null, marcador: Element): string {
  if (!label) return '';
  let r = '';
  const w = document.createTreeWalker(label, NodeFilter.SHOW_TEXT);
  for (let n = w.nextNode(); n; n = w.nextNode()) if (!marcador.contains(n)) r += n.textContent ?? '';
  return r.replace(/\s*\*\s*$/, '').replace(/\s+/g, ' ').trim();
}

/** Rótulo do campo lido do DOM: `<label for>` ou o `<label>` em volta. */
function rotuloDoMarcador(el: HTMLElement): string {
  const porFor = el.id ? document.querySelector(`label[for="${CSS.escape(el.id)}"]`) : null;
  return textoDoRotulo(porFor, el) || textoDoRotulo(el.closest('label'), el);
}

// Clique no rótulo de um marcador: o `<label for>` só ativa elemento rotulável, então o foco vai ao marcador por aqui (e ele vira
// o campo real). Um ouvinte só, no documento.
let ouvinteDeRotulo = false;
function ouvirRotulos() {
  if (ouvinteDeRotulo || typeof document === 'undefined') return;
  ouvinteDeRotulo = true;
  document.addEventListener('click', (e) => {
    const label = (e.target as Element | null)?.closest?.('label');
    if (!label) return;
    const alvo = label.htmlFor ? document.getElementById(label.htmlFor) : label.querySelector<HTMLElement>('[data-marcador]');
    if (alvo?.hasAttribute('data-marcador') && alvo.getAttribute('aria-disabled') !== 'true') alvo.focus();
  });
}

/**
 * Marcador do campo sensível sem foco (review r12, decisão que substitui o desfoque da r11; visual M4, M5 e B3, spec B1): com o
 * modo discreto ligado, o campo sem foco NÃO renderiza o input. No lugar fica esta caixa com a mesma classe do campo (borda
 * `border-borda-campo` de 3:1, fundo e altura), a marca `•••••` de largura fixa (não revela o tamanho do valor), parada de Tab
 * e o nome "<rótulo>: valor oculto". Assim a árvore de acessibilidade nunca recebe o valor real. Ao receber o foco (Tab, clique,
 * toque ou clique no rótulo), `aoAtivar` troca pelo input real, já focado com o cursor no fim. O valor nunca sai do estado do
 * formulário: salvar sem tocar no campo envia o real. Na impressão sai o marcador.
 */
function MarcadorSensivel({ id, rotulo, title, className, linhas, desabilitado, aoAtivar }: { id?: string; rotulo?: string; title?: string; className: string; linhas?: number; desabilitado?: boolean; aoAtivar: () => void }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [lido, setLido] = useState('');
  useLayoutEffect(() => {
    ouvirRotulos();
    if (!rotulo && ref.current) setLido(rotuloDoMarcador(ref.current));
  }, [rotulo, id]);
  const nome = rotulo || lido;
  const direita = /(^|\s)text-right(\s|$)/.test(className);
  return (
    <div
      ref={ref}
      id={id}
      role="textbox"
      tabIndex={desabilitado ? -1 : 0}
      aria-label={nome ? t('discreto.campoOculto', { rotulo: nome }) : t('discreto.valorOculto')}
      aria-disabled={desabilitado || undefined}
      title={title}
      data-mascarado=""
      data-marcador=""
      onFocus={() => !desabilitado && aoAtivar()}
      className={`${className} flex cursor-text overflow-hidden select-none ${linhas ? 'items-start' : 'items-center'} ${direita ? 'justify-end' : ''} ${desabilitado ? 'opacity-60' : ''}`}
    >
      <span aria-hidden className="block shrink-0 tracking-widest text-tinta-2" style={linhas ? { height: `${linhas}lh` } : undefined}>
        •••••
      </span>
    </div>
  );
}

/**
 * Campo sensível (modo discreto). O `value` é SEMPRE o real e vive no estado do formulário (review r11, spec M1 e visual M2:
 * trocar o `value` no foco quebrava cursor e desfazer). Review r12: sem foco, com o modo ligado e algo a esconder, o campo vira
 * o `MarcadorSensivel` (sem input no DOM); o foco no marcador devolve o input real, focado com o cursor no fim, e o cursor, a
 * seleção, colar, desfazer e refazer seguem os nativos enquanto ele tem o foco. `rotulo` dá o nome do marcador; sem ele, o
 * marcador lê o `<label>` do campo (ou usa o `aria-label`).
 */
export function EntradaSensivel({ mascara, livre = false, nomesNoTexto = [], rotulo, value, onFocus, onBlur, ...props }: React.InputHTMLAttributes<HTMLInputElement> & { mascara: MascaraCampo | null; livre?: boolean; nomesNoTexto?: (string | null | undefined)[]; rotulo?: string }) {
  const { ativo, nomes } = useDiscreto();
  const [foco, setFoco] = useState(false);
  const entrada = useRef<HTMLInputElement>(null);
  const revelar = useRef(false);
  const real = value === null || value === undefined ? '' : String(value);
  const mascarado = !foco && !invalido(props['aria-invalid']) && campoMascarado(real, mascara, livre, nomesNoTexto, ativo, nomes);
  useLayoutEffect(() => {
    if (!revelar.current || mascarado) return;
    revelar.current = false;
    focarNoFim(entrada.current);
  });
  if (mascarado)
    return (
      <MarcadorSensivel
        id={props.id}
        rotulo={rotulo ?? props['aria-label']}
        title={props.title}
        className={`${classeEntrada} ${props.className ?? ''}`}
        desabilitado={props.disabled}
        aoAtivar={() => {
          revelar.current = true;
          setFoco(true);
        }}
      />
    );
  return (
    <Entrada
      {...props}
      ref={entrada}
      value={value}
      onFocus={(e) => {
        setFoco(true);
        onFocus?.(e);
      }}
      onBlur={(e) => {
        setFoco(false);
        onBlur?.(e);
      }}
    />
  );
}

/**
 * Lista B de 30/10 (item 4): campo com máscara de formato enquanto digita (telefone, documento, CEP, valores). A máscara vem de
 * `nucleo/mascaras.ts`; aqui o texto digitado ou colado passa por ela e o cursor fica no lugar (digitar no meio não o joga para o
 * fim). Passa pelo `EntradaSensivel`: no modo discreto o campo preenchido e sem foco vira o marcador, que não mostra nada do
 * formato; o exemplo do placeholder é constante e só aparece com o campo vazio.
 */
export function EntradaComMascara({
  formato,
  lado,
  valor,
  aoMudarTexto,
  mascara,
  ...props
}: Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> & { formato: FormatoMascara; lado?: LadoCursor; valor: string; aoMudarTexto: (texto: string) => void; mascara: MascaraCampo | null; livre?: boolean; nomesNoTexto?: (string | null | undefined)[]; rotulo?: string }) {
  const aoMudar = useMascaraDigitando({ formato, lado, valor, aoMudarTexto });
  return <EntradaSensivel {...props} mascara={mascara} value={valor} onChange={aoMudar} />;
}

/**
 * Área de texto livre com a mesma regra do campo: sem foco, com o modo ligado e algo a esconder, vira o marcador. Com `mascara`
 * (review r16 spec M3: o CSV colado no diálogo de importação, que é dado estruturado de cadastro, nome, documento e valor, e não
 * texto livre), vale a regra do campo com máscara do `campoMascarado`: com o modo ligado, sempre o marcador sem foco.
 */
export function AreaSensivel({ nomesNoTexto = [], rotulo, mascara = null, value, onFocus, onBlur, ...props }: React.TextareaHTMLAttributes<HTMLTextAreaElement> & { nomesNoTexto?: (string | null | undefined)[]; rotulo?: string; mascara?: MascaraCampo | null }) {
  const { ativo, nomes } = useDiscreto();
  const [foco, setFoco] = useState(false);
  const area = useRef<HTMLTextAreaElement>(null);
  const revelar = useRef(false);
  const real = value === null || value === undefined ? '' : String(value);
  const mascarado = !foco && !invalido(props['aria-invalid']) && campoMascarado(real, mascara, true, nomesNoTexto, ativo, nomes);
  useLayoutEffect(() => {
    if (!revelar.current || mascarado) return;
    revelar.current = false;
    focarNoFim(area.current);
  });
  if (mascarado)
    return (
      <MarcadorSensivel
        id={props.id}
        rotulo={rotulo ?? props['aria-label']}
        title={props.title}
        className={props.className ?? ''}
        linhas={props.rows ?? 2}
        desabilitado={props.disabled}
        aoAtivar={() => {
          revelar.current = true;
          setFoco(true);
        }}
      />
    );
  return (
    <textarea
      {...props}
      ref={area}
      value={value}
      onFocus={(e) => {
        setFoco(true);
        onFocus?.(e);
      }}
      onBlur={(e) => {
        setFoco(false);
        onBlur?.(e);
      }}
    />
  );
}

const ATRASO_DATA_MS = 250;

/**
 * Campo de data, mês, data e hora ou hora no formato do idioma do app (nunca o do navegador).
 * O que se vê é um campo de texto com máscara do idioma (dd/mm/aaaa em pt e es, mm/dd/yyyy em en), com foco visível;
 * o botão ao lado abre o seletor nativo. O valor guardado segue em ISO e só é entregue a `onChange` quando o texto
 * está completo e válido (ano de 1900 a 2100), com pequeno atraso: filtro nunca dispara com data pela metade.
 * Com `rotulo`, mostra o rótulo visível acima do campo (filtros de período).
 */
export function CampoData({ type, rotulo, className = '', sensivel, ...props }: Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> & { type: TipoData; rotulo?: string; sensivel?: boolean }) {
  const { t, i18n: i } = useTranslation();
  const { ativo: discreto } = useDiscreto();
  const [comFoco, setComFoco] = useState(false);
  const idioma = (['pt', 'en', 'es'].includes(i.language) ? i.language : 'pt') as IdiomaCampo;
  const gerado = useId();
  const { value, onChange, id: idProp, min, max, disabled, onFocus, onBlur, onKeyDown, ...resto } = props;
  const id = idProp ?? gerado;
  const valor = String(value ?? '');
  const marca: Record<TipoData, string> = { date: t('comum.formatoData'), month: t('comum.formatoMes'), 'datetime-local': t('comum.formatoDataHora'), time: t('comum.formatoHora') };
  const Icone = type === 'time' ? Clock : CalendarDays;
  const nativo = useRef<HTMLInputElement>(null);
  const focado = useRef(false);
  const pendente = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [texto, setTexto] = useState(() => textoDoValor(type, valor, idioma));
  const [saiu, setSaiu] = useState(false);
  const campoTexto = useRef<HTMLInputElement>(null);

  // Valor vindo de fora (seletor, limpar filtro, abrir registro) reescreve o texto no idioma atual. Durante a digitação o texto
  // manda: o valor que o próprio campo acabou de entregar já corresponde ao texto e não o reescreve.
  const textoAtual = useRef(texto);
  textoAtual.current = texto;
  // Rodada rrl-4 (VIS-M1): o Tab sem pausa do Início entrega o valor ao sair, e o Fim dependente é reescrito. Com efeito passivo, a
  // reescrita vinha depois do Tab e trocava a seleção por cursor no fim, e a máscara cheia recusava o que se digitava. Efeito de
  // layout reescreve antes do foco chegar; se o foco já chegou com o texto todo selecionado, seleciona de novo. Só nesse caso:
  // cursor no meio da digitação não muda.
  const selecionarDeNovo = useRef(false);
  useLayoutEffect(() => {
    if (focado.current && valorDoTexto(type, textoAtual.current, idioma) === valor) return;
    if (focado.current && valor === '' && textoAtual.current !== '' && valorDoTexto(type, textoAtual.current, idioma) === null) return;
    const novo = textoDoValor(type, valor, idioma);
    const el = campoTexto.current;
    selecionarDeNovo.current = !!el && focado.current && novo !== textoAtual.current && el.selectionStart === 0 && el.selectionEnd === el.value.length;
    setTexto(novo);
    setSaiu(false);
  }, [valor, type, idioma]);
  useLayoutEffect(() => {
    if (!selecionarDeNovo.current) return;
    selecionarDeNovo.current = false;
    campoTexto.current?.select();
  }, [texto]);

  /** Entrega o valor ao pai pelo input nativo, para que `onChange` receba um evento de verdade (`e.target.value`). */
  function emitir(novo: string) {
    const el = nativo.current;
    if (!el || el.value === novo) return;
    const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
    setter?.call(el, novo);
    el.dispatchEvent(new Event('input', { bubbles: true }));
  }

  function agendar(novo: string, imediato = false) {
    if (pendente.current) clearTimeout(pendente.current);
    if (imediato) emitir(novo);
    else pendente.current = setTimeout(() => emitir(novo), ATRASO_DATA_MS);
  }

  function aoDigitar(bruto: string) {
    const masc = mascarar(type, bruto);
    setTexto(masc);
    setSaiu(false);
    if (!masc) {
      if (valor !== '') agendar('', true);
      return;
    }
    const iso = valorDoTexto(type, masc, idioma);
    const aceito = iso !== null && dentroDoIntervalo(iso, min, max);
    if (iso !== null && aceito && iso !== valor) agendar(iso);
    else if (pendente.current) clearTimeout(pendente.current);
  }

  // Modo discreto (r9 visual B1): data sensível (nascimento) fica oculta sem foco, como o campo de dinheiro.
  const situacao = estadoDoTexto(type, texto, idioma, min, max);
  const ruim = situacao === 'incompleto' || situacao === 'invalido';
  // Campo com erro de validação não é mascarado (r10 visual B10): "•••••" com "Data inválida" embaixo não dá para corrigir.
  const mascarado = !!sensivel && discreto && !comFoco && texto !== '' && !ruim && !(props['aria-invalid'] && props['aria-invalid'] !== 'false');
  // Erro visível: data completa que não existe, ou texto pela metade depois de sair do campo. Erro vindo de fora (formulário) também vale.
  const erroExterno = !!props['aria-invalid'] && props['aria-invalid'] !== 'false';
  const mostrarErro = (situacao === 'invalido' || (situacao === 'incompleto' && saiu)) && !erroExterno;
  const idErro = `${id}-data-erro`;
  // Validade nativa: um <form> sem noValidate também deixa de enviar enquanto a data estiver errada.
  useEffect(() => {
    campoTexto.current?.setCustomValidity(ruim ? t('comum.dataInvalida') : '');
  }, [ruim, t]);

  function abrirSeletor() {
    try {
      nativo.current?.showPicker?.();
    } catch {
      /* sem seletor nativo: a digitação continua valendo */
    }
  }

  // Review r12: data sensível sem foco vira o marcador (sem input no DOM); o foco nele devolve o campo real com o cursor no fim.
  const revelar = useRef(false);
  useLayoutEffect(() => {
    if (!revelar.current || mascarado) return;
    revelar.current = false;
    focarNoFim(campoTexto.current);
  });

  const campo = (
    <div className={`relative min-w-0 ${className}`}>
      {mascarado ? (
        <MarcadorSensivel
          id={id}
          rotulo={rotulo ?? (resto['aria-label'] as string | undefined)}
          className={`${classeEntrada} numero pr-12 md:pr-10`}
          desabilitado={disabled}
          aoAtivar={() => {
            revelar.current = true;
            setComFoco(true);
          }}
        />
      ) : (
        <input
          {...resto}
          ref={campoTexto}
          id={id}
          type="text"
          data-data-invalida={ruim ? '' : undefined}
          inputMode="numeric"
          autoComplete="off"
          lang={i.language}
          disabled={disabled}
          value={texto}
          placeholder={marca[type]}
          title={props.title ?? rotulo}
          aria-invalid={mostrarErro || erroExterno ? true : undefined}
          aria-describedby={mostrarErro ? idErro : props['aria-describedby']}
          onChange={(e) => aoDigitar(e.target.value)}
          onFocus={(e) => {
            focado.current = true;
            setComFoco(true);
            onFocus?.(e);
          }}
          onInvalid={() => setSaiu(true)}
          onKeyDown={(e) => {
            onKeyDown?.(e);
            if (e.key === 'Enter') {
              const iso = valorDoTexto(type, texto, idioma);
              if (iso !== null && iso !== valor && dentroDoIntervalo(iso, min, max)) agendar(iso, true);
            }
          }}
          onBlur={(e) => {
            focado.current = false;
            setComFoco(false);
            const iso = valorDoTexto(type, texto, idioma);
            if (iso !== null && dentroDoIntervalo(iso, min, max) && iso !== valor) agendar(iso, true);
            else if (pendente.current) clearTimeout(pendente.current);
            // Texto válido é reescrito no formato do idioma; o inválido fica como foi digitado, marcado com o erro (nada de reverter em silêncio).
            if (iso !== null && dentroDoIntervalo(iso, min, max)) {
              setTexto(textoDoValor(type, iso, idioma));
            } else setSaiu(true);
            onBlur?.(e);
          }}
          className={`${classeEntrada} numero pr-12 md:pr-10`}
        />
      )}
      <button
        type="button"
        disabled={disabled}
        tabIndex={-1}
        aria-label={`${t('comum.abrirSeletor')}${rotulo ? `: ${rotulo}` : ''}`}
        onClick={abrirSeletor}
        className="absolute top-0 right-0 flex size-11 items-center justify-center rounded-r-lg text-tinta-3 hover:text-tinta-1 disabled:opacity-60 md:size-10"
      >
        <Icone className="size-4" aria-hidden />
      </button>
      {mostrarErro && (
        <p id={idErro} role="alert" className="mt-1 text-xs text-perigo">
          {t('comum.dataInvalida')}
        </p>
      )}
      <input
        ref={nativo}
        type={type}
        tabIndex={-1}
        aria-hidden
        lang={i.language}
        min={min}
        max={max}
        disabled={disabled}
        value={mascarado ? '' : valor}
        onChange={(e) => onChange?.(e)}
        className="pointer-events-none absolute right-0 bottom-0 size-px opacity-0"
      />
    </div>
  );
  if (!rotulo) return campo;
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <label htmlFor={id} className="text-xs font-medium text-tinta-3">
        {rotulo}
      </label>
      {campo}
    </div>
  );
}

export function Selecao({
  opcoes,
  vazio,
  vazioDesabilitado,
  icone,
  ...props
}: React.SelectHTMLAttributes<HTMLSelectElement> & {
  opcoes: Opcao[];
  vazio?: string;
  /** Review rval1-11 visual (M1): a opção vazia só como aviso ("Selecione" do campo obrigatório ainda sem valor), não escolhível. */
  vazioDesabilitado?: boolean;
  ref?: React.Ref<HTMLSelectElement>;
  /** Ajustes do CRM 27/10 (K1), opt-in: ícone à esquerda do valor (os seletores da linha de filtros do Comercial). */
  icone?: React.ReactNode;
}) {
  return (
    <div className="relative">
      {icone && <span className="pointer-events-none absolute top-1/2 left-3 flex -translate-y-1/2 text-tinta-3 print:hidden" aria-hidden>{icone}</span>}
      <select {...props} className={`${classeEntrada} appearance-none pr-9 ${icone ? 'pl-9' : ''} ${props.className ?? ''}`}>
        {vazio !== undefined && <option value="" disabled={vazioDesabilitado}>{vazio}</option>}
        {opcoes.map((o) => (
          <option key={o.valor} value={o.valor} disabled={o.desabilitado}>
            {o.rotulo}
          </option>
        ))}
      </select>
      <ChevronDown className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-tinta-3 print:hidden" aria-hidden />
    </div>
  );
}

/**
 * Lista com a regra do campo sensível (review r15 spec M1: campo personalizado do tipo lista marcado como sensível): sem foco,
 * com o modo ligado e um valor escolhido, vira o `MarcadorSensivel` (o rótulo da opção não fica no DOM); o foco no marcador
 * devolve a lista real, já focada. Review r18 spec (B3): sem valor escolhido, a lista nativa ficava montada com os rótulos
 * crus (as agendas do Google da W26, com e-mail e telefone no nome, no DOM e no leitor de tela); agora, com o modo ligado e
 * sem foco, os rótulos passam pela regra do texto livre (`textoLivre`: e-mail, telefone, documento e dinheiro mascarados) e
 * o foco, que já revela o campo por regra, traz os rótulos reais.
 */
export function SelecaoSensivel({ mascara, rotulo, onFocus, onBlur, ...props }: React.ComponentProps<typeof Selecao> & { mascara: MascaraCampo | null; rotulo?: string }) {
  const { ativo, nomes } = useDiscreto();
  const [foco, setFoco] = useState(false);
  const lista = useRef<HTMLSelectElement>(null);
  const revelar = useRef(false);
  const real = props.value === null || props.value === undefined ? '' : String(props.value);
  const mascarado = !foco && !invalido(props['aria-invalid']) && campoMascarado(real, mascara, false, [], ativo, nomes);
  useLayoutEffect(() => {
    if (!revelar.current || mascarado) return;
    revelar.current = false;
    lista.current?.focus();
  });
  if (mascarado)
    return (
      <MarcadorSensivel
        id={props.id}
        rotulo={rotulo ?? props['aria-label']}
        title={props.title}
        className={`${classeEntrada} ${props.className ?? ''}`}
        desabilitado={props.disabled}
        aoAtivar={() => {
          revelar.current = true;
          setFoco(true);
        }}
      />
    );
  const opcoes = ativo && mascara && !foco ? props.opcoes.map((o) => ({ ...o, rotulo: textoLivre(o.rotulo) })) : props.opcoes;
  return (
    <Selecao
      {...props}
      opcoes={opcoes}
      ref={lista}
      onFocus={(e) => {
        setFoco(true);
        onFocus?.(e);
      }}
      onBlur={(e) => {
        setFoco(false);
        onBlur?.(e);
      }}
    />
  );
}

export function Alternador({ id, marcado, aoMudar, rotulo, desabilitado, invalido }: { id?: string; marcado: boolean; aoMudar: (v: boolean) => void; rotulo?: string; desabilitado?: boolean; invalido?: boolean }) {
  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={marcado}
      aria-label={rotulo}
      aria-invalid={invalido || undefined}
      disabled={desabilitado}
      onClick={() => aoMudar(!marcado)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border border-borda-campo transition-colors disabled:opacity-50 ${marcado ? 'bg-acento-forte' : 'bg-sup-3'}`}
    >
      <span className={`inline-block size-4 rounded-full shadow transition-transform ${marcado ? 'translate-x-6 bg-sup-1' : 'translate-x-1 bg-tinta-3'}`} />
    </button>
  );
}

/**
 * Campo de dinheiro (texto no idioma, valor em centavos inteiros). Fase 2 (W36): exportado para o filtro de valor das listas.
 * Item 4 da lista B de 30/10: a máscara se forma enquanto digita (só os dígitos contam, entram da direita: 5 vira 0,05) e o
 * exemplo "R$ 0,00" fica no placeholder.
 */
export function EntradaDinheiro({ id, valor, aoMudar, placeholder, ...resto }: { id: string; valor: number | null; aoMudar: (v: number | null) => void; disabled?: boolean; placeholder?: string; 'aria-invalid'?: boolean; 'aria-describedby'?: string }) {
  const [texto, setTexto] = useState(deCentavos(valor));
  const focado = useRef(false);
  useEffect(() => {
    if (!focado.current) setTexto(deCentavos(valor));
  }, [valor]);
  return (
    <EntradaComMascara
      mascara="dinheiro"
      id={id}
      inputMode="numeric"
      autoComplete="off"
      className="numero text-right"
      placeholder={placeholder ?? EXEMPLOS_MASCARA.dinheiro()}
      formato={mascaraDinheiro}
      lado="direita"
      valor={texto}
      aoMudarTexto={(t) => {
        setTexto(t);
        aoMudar(centavosDoTexto(t));
      }}
      onFocus={() => (focado.current = true)}
      onBlur={() => {
        focado.current = false;
        setTexto(deCentavos(centavosDoTexto(texto)));
      }}
      {...resto}
    />
  );
}

/**
 * Campo de percentual (valor em número: 12,5 para "12,50 %"). Item 4 da lista B de 30/10: a máscara se forma enquanto digita (só os
 * dígitos contam, duas casas, " %" no fim); `max` limita o que se digita (um dígito que passaria do máximo é recusado).
 */
export function EntradaPercentual({ id, valor, aoMudar, max, placeholder, ...resto }: { id: string; valor: number | null; aoMudar: (v: number | null) => void; max?: number; disabled?: boolean; placeholder?: string; 'aria-invalid'?: boolean; 'aria-describedby'?: string }) {
  const [texto, setTexto] = useState(textoDoPercentual(valor));
  const focado = useRef(false);
  useEffect(() => {
    if (!focado.current) setTexto(textoDoPercentual(valor));
  }, [valor]);
  return (
    <EntradaComMascara
      mascara={null}
      id={id}
      inputMode="numeric"
      autoComplete="off"
      className="numero text-right"
      placeholder={placeholder ?? EXEMPLOS_MASCARA.percentual()}
      formato={mascaraPercentual}
      lado="direita"
      valor={texto}
      aoMudarTexto={(t) => {
        const n = percentualDoTexto(t);
        if (n !== null && max !== undefined && n > max) return;
        setTexto(t);
        aoMudar(n);
      }}
      onFocus={() => (focado.current = true)}
      onBlur={() => {
        focado.current = false;
        setTexto(textoDoPercentual(percentualDoTexto(texto)));
      }}
      {...resto}
    />
  );
}

/**
 * Select assíncrono para relacionamentos (cliente, produto, responsável), no padrão combobox (ARIA): setas
 * movem a opção ativa (`aria-activedescendant`), Enter escolhe, Esc fecha, e sair do campo (Tab ou clique fora)
 * fecha a lista. As opções não entram na ordem de Tab (o foco fica sempre no campo).
 * r7 visual M5: a lista abre ao digitar, com seta para baixo (ou Alt+seta para baixo) ou com clique, e nunca só por receber
 * o foco (o Tab atravessava o formulário abrindo cada lista). Ao receber o foco o texto fica selecionado: digitar substitui.
 */
/** Busca de pessoa ou empresa (parceiro, funcionário): com a opção de nomes do modo discreto, o rótulo vira iniciais. */
const CAMINHOS_DE_NOME = /^\/(cadastros\/parceiros|pessoas\/funcionarios)(\/|$)/;

export function BuscaRemota({ id, def, valor, rotuloAtual, aoMudar, placeholder, listaNoFluxo, limparAoEscolher, ...aria }: { id: string; def: NonNullable<DefCampo['busca']>; valor: string | null; rotuloAtual?: string; aoMudar: (v: string | null, item?: Record<string, unknown>) => void; 'aria-invalid'?: boolean; 'aria-describedby'?: string; /** Fase 2 (W36): texto do campo vazio (padrão "Buscar"). */ placeholder?: string; /** Lista de 02/11: dentro de pop-up curto a lista entra no fluxo, com altura fixa, em vez de flutuar e ser cortada pela rolagem do corpo. */ listaNoFluxo?: boolean; /** A escolha vai para uma lista de quem chama, e o campo volta vazio para a próxima busca. */ limparAoEscolher?: boolean }) {
  const { t } = useTranslation();
  useDiscreto();
  const mostrar = (texto: string) => (def.pessoa || CAMINHOS_DE_NOME.test(def.caminho) ? nomePessoa(texto) : texto);
  const [aberto, setAberto] = useState(false);
  const [termo, setTermo] = useState('');
  const [rotulo, setRotulo] = useState(rotuloAtual ?? '');
  // Item 12 (21/10): o valor escolhido por fora (o lead criado no cadastro rápido) chega com o rótulo novo.
  useEffect(() => {
    if (rotuloAtual) setRotulo(rotuloAtual);
  }, [rotuloAtual]);
  const [ativa, setAtiva] = useState(-1);
  const caixa = useRef<HTMLDivElement>(null);
  const idLista = `${id}-opcoes`;
  const valorDe = def.valor ?? ((i: Record<string, unknown>) => String(i.id));
  const q = useQuery({
    queryKey: ['busca', def.caminho, def.params, termo],
    queryFn: () => get<{ itens: Record<string, unknown>[] }>(def.caminho, { ...def.params, busca: termo || undefined, limite: 20 }),
    enabled: aberto,
  });
  const itens = q.data?.itens ?? [];
  // Resolve o rótulo quando o formulário abre já com um valor.
  const inicial = useQuery({
    queryKey: ['busca-id', def.caminho, valor],
    queryFn: () => get<{ itens: Record<string, unknown>[] }>(def.caminho, { ...def.params, ids: valor, limite: 1 }),
    enabled: !!valor && !rotulo,
  });
  useEffect(() => {
    const item = inicial.data?.itens.find((i) => valorDe(i) === valor);
    if (item) setRotulo(def.rotulo(item));
  }, [inicial.data]); // eslint-disable-line react-hooks/exhaustive-deps
  // Correções de 02/11 (R-CORR1-VIS-M1, ROB-M3): com a lista no fluxo, fechar encolhe o pop-up. Se fechasse ao apertar o botão do
  // mouse, o que está abaixo subiria antes de soltar e o clique se perderia; por isso o clique fora só fecha depois do `click`. O
  // teclado (Tab, Esc) segue fechando na hora.
  const ponteiroFora = useRef(false);
  // rcorr-3: com a lista fechada, o clique no rótulo não arma o fechamento adiado (ele fecharia o que o clique repassado acabou de abrir).
  const listaAberta = useRef(aberto);
  useEffect(() => {
    listaAberta.current = aberto;
  }, [aberto]);
  useEffect(() => {
    let soltar: (() => void) | null = null;
    const parar = () => {
      if (soltar) window.removeEventListener('mouseup', soltar, true);
      soltar = null;
    };
    const fora = (e: MouseEvent) => {
      if (caixa.current?.contains(e.target as Node)) return;
      if (!listaNoFluxo) return setAberto(false);
      if (!listaAberta.current) return;
      parar();
      ponteiroFora.current = true;
      soltar = () => {
        parar();
        window.setTimeout(() => {
          ponteiroFora.current = false;
          setAberto(false);
        }, 0);
      };
      window.addEventListener('mouseup', soltar, true);
    };
    document.addEventListener('mousedown', fora, listaNoFluxo);
    return () => {
      parar();
      document.removeEventListener('mousedown', fora, listaNoFluxo);
    };
  }, [listaNoFluxo]);
  // A lista nova (outro termo) recomeça sem opção ativa.
  useEffect(() => setAtiva(-1), [termo, q.data]);
  useEffect(() => {
    if (ativa >= 0) document.getElementById(`${idLista}-${ativa}`)?.scrollIntoView({ block: 'nearest' });
  }, [ativa, idLista]);

  function escolher(item: Record<string, unknown>) {
    if (!limparAoEscolher) setRotulo(def.rotulo(item));
    aoMudar(valorDe(item), item);
    setAberto(false);
  }
  function abrir() {
    if (aberto) return;
    setAberto(true);
    setTermo('');
  }
  function aoTeclar(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowUp' && e.altKey) {
      e.preventDefault();
      setAberto(false);
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!aberto) return abrir();
      if (e.altKey) return;
      if (!itens.length) return;
      setAtiva((a) => (e.key === 'ArrowDown' ? (a + 1) % itens.length : a <= 0 ? itens.length - 1 : a - 1));
    } else if (e.key === 'Enter' && aberto) {
      // Enter nunca envia o formulário com a lista aberta: escolhe a opção ativa (ou só ignora).
      e.preventDefault();
      const item = itens[ativa];
      if (item) escolher(item);
    } else if (e.key === 'Escape' && aberto) {
      e.stopPropagation();
      e.nativeEvent.stopImmediatePropagation();
      setAberto(false);
    } else if ((e.key === 'Home' || e.key === 'End') && aberto && itens.length) {
      e.preventDefault();
      setAtiva(e.key === 'Home' ? 0 : itens.length - 1);
    }
  }
  return (
    <div
      ref={caixa}
      className="relative"
      // Sair da combobox fecha a lista; foco que vai para dentro dela (lista, limpar) não fecha (r6 visual A1).
      onBlur={(e) => {
        if (e.relatedTarget instanceof Node && caixa.current?.contains(e.relatedTarget)) return;
        if (!ponteiroFora.current) setAberto(false);
      }}
    >
      <div className="relative">
        <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-tinta-3" aria-hidden />
        <Entrada
          id={id}
          {...aria}
          role="combobox"
          aria-expanded={aberto}
          aria-controls={aberto ? idLista : undefined}
          aria-autocomplete="list"
          aria-activedescendant={aberto && ativa >= 0 ? `${idLista}-${ativa}` : undefined}
          autoComplete="off"
          className="pr-10 pl-9"
          value={aberto ? termo : mostrar(rotulo)}
          placeholder={placeholder ?? t('comum.buscar')}
          onFocus={(e) => e.currentTarget.select()}
          onClick={abrir}
          onKeyDown={aoTeclar}
          onChange={(e) => {
            setAberto(true);
            setTermo(e.target.value);
          }}
        />
        {valor && (
          <button
            type="button"
            tabIndex={-1}
            aria-label={t('comum.limpar')}
            className="absolute top-1/2 right-1 flex size-9 -translate-y-1/2 items-center justify-center rounded text-tinta-3 hover:text-tinta-1"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              setRotulo('');
              aoMudar(null);
            }}
          >
            <X className="size-4" />
          </button>
        )}
      </div>
      {aberto && (
        <>
          <span role="status" className="sr-only">
            {q.data ? t('busca.resultados', { count: itens.length }) : ''}
          </span>
          {/* mouseDown com preventDefault: clicar numa opção não tira o foco do campo (o blur fecharia a lista antes do clique).
              tabIndex -1 (r6 visual A1): a lista com rolagem virava parada de Tab no Chrome; o Tab saía do campo para ela, o blur
              fechava a lista, o foco caía no body e o trap o mandava ao Fechar. Assim o Tab vai direto ao próximo campo. */}
          <ul id={idLista} role="listbox" tabIndex={-1} aria-label={t('comum.buscar')} onMouseDown={(e) => e.preventDefault()} className={`${listaNoFluxo ? 'relative h-64' : 'absolute max-h-64'} z-30 mt-1 w-full overflow-y-auto rounded-lg border border-borda bg-sup-2 py-1 shadow-xl`}>
            {q.isPending && (
              <li role="presentation" className="px-3 py-2 text-sm text-tinta-3">
                {t('comum.carregando')}
              </li>
            )}
            {q.data?.itens.length === 0 && (
              <li role="presentation" className="px-3 py-2 text-sm text-tinta-3">
                {t('estados.semResultado')}
              </li>
            )}
            {itens.map((item, i) => (
              <TextoCortado
                como="li"
                key={valorDe(item)}
                id={`${idLista}-${i}`}
                role="option"
                aria-selected={valorDe(item) === valor}
                // r8 visual B3: a opção já escolhida tem marca própria além da cor (barra de 2px acento-forte, 3:1 ou mais).
                data-escolhida={valorDe(item) === valor || undefined}
                className={`w-full cursor-pointer border-l-2 px-3 py-2 text-left text-sm hover:bg-sup-3 ${valorDe(item) === valor ? 'border-l-acento-forte font-semibold text-tinta-1' : 'border-l-transparent'} ${i === ativa ? 'bg-sup-3 outline-2 -outline-offset-2 outline-foco' : ''}`}
                // rcorr-2: dentro de <label>, o clique seria repassado ao campo e reabriria a lista.
                onClick={(e) => {
                  e.preventDefault();
                  escolher(item);
                }}
              >
                {mostrar(def.rotulo(item))}
              </TextoCortado>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

export function CampoControlado({ def, valor, aoMudar, erro, rotuloAtual }: { def: DefCampo; valor: unknown; aoMudar: (v: unknown) => void; erro?: string; rotuloAtual?: string }) {
  const id = useId();
  const aria = { 'aria-invalid': !!erro, 'aria-describedby': erro ? `${id}-erro` : undefined };
  let controle: ReactNode;
  switch (def.tipo) {
    case 'area':
      controle = (
        <AreaSensivel
          id={id}
          {...aria}
          nomesNoTexto={def.nomes}
          rows={4}
          disabled={def.desabilitado}
          placeholder={def.placeholder}
          value={(valor as string) ?? ''}
          onChange={(e) => aoMudar(e.target.value)}
          className="w-full min-w-0 rounded-lg border border-borda-campo bg-campo px-3 py-2 text-sm text-tinta-1 placeholder:text-tinta-3 focus:border-foco focus:outline-2 focus:outline-offset-0 focus:outline-foco"
        />
      );
      break;
    case 'selecao':
      controle = (
        <SelecaoSensivel
          mascara={mascaraDoCampo(def)}
          id={id}
          {...aria}
          disabled={def.desabilitado}
          opcoes={def.opcoes ?? []}
          // Review rval1-11 visual (M1): o obrigatório sem valor mostrava a 1a opção com o estado vazio ("Preencha o campo" com
          // a tela dizendo o contrário, e escolher a mesma opção não disparava a mudança). Agora mostra "Selecione", desabilitado.
          vazio={!def.obrigatorio || (vazioNoForm(valor) && !(def.opcoes ?? []).some((o) => o.valor === '')) ? (def.placeholder ?? i18n.t('comum.selecione')) : undefined}
          vazioDesabilitado={!!def.obrigatorio}
          value={(valor as string) ?? ''}
          onChange={(e) => aoMudar(e.target.value || null)}
        />
      );
      break;
    case 'booleano':
      controle = (
        <div className="flex h-10 items-center">
          <Alternador id={id} rotulo={def.rotulo} marcado={!!valor} aoMudar={aoMudar} desabilitado={def.desabilitado} />
        </div>
      );
      break;
    case 'dinheiro':
      controle = <EntradaDinheiro id={id} {...aria} valor={(valor as number | null) ?? null} aoMudar={aoMudar} disabled={def.desabilitado} placeholder={def.placeholder} />;
      break;
    case 'percentual':
      controle = <EntradaPercentual id={id} {...aria} valor={valor === null || valor === undefined || valor === '' || !Number.isFinite(Number(valor)) ? null : Number(valor)} aoMudar={aoMudar} max={def.max} disabled={def.desabilitado} placeholder={def.placeholder} />;
      break;
    case 'documento':
    case 'cep':
      controle = <EntradaMascarada def={def} id={id} aria={aria} valor={valor} aoMudar={aoMudar} />;
      break;
    case 'busca':
      controle = <BuscaRemota id={id} {...aria} def={def.busca!} valor={(valor as string) ?? null} rotuloAtual={rotuloAtual} aoMudar={(v) => aoMudar(v)} />;
      break;
    case 'tags':
      controle = (
        <Entrada
          id={id}
          {...aria}
          disabled={def.desabilitado}
          placeholder={def.placeholder}
          value={Array.isArray(valor) ? valor.join(', ') : ''}
          onChange={(e) =>
            aoMudar(
              e.target.value
                .split(',')
                .map((s) => s.trimStart())
                .filter((s, i, a) => s || i === a.length - 1),
            )
          }
          onBlur={(e) => aoMudar(e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
        />
      );
      break;
    case 'numero':
      // Review r14 (spec M2): número com nome sensível (`salario`, `valor_contrato`, `cep_cobranca`) vira o marcador sem foco.
      // Review r15 (spec M1): o número do campo personalizado vira o marcador sempre (`mascaraDoCampo`, regra efetiva).
      controle = (
        <EntradaSensivel
          mascara={mascaraDoCampo(def)}
          id={id}
          {...aria}
          type="number"
          className="numero"
          min={def.min}
          max={def.max}
          step={def.passo ?? 'any'}
          disabled={def.desabilitado}
          value={valor === null || valor === undefined ? '' : String(valor)}
          onChange={(e) => aoMudar(e.target.value === '' ? null : Number(e.target.value))}
        />
      );
      break;
    case 'data':
    case 'datahora':
    case 'hora':
      controle = (
        <CampoData id={id} {...aria} type={def.tipo === 'data' ? 'date' : def.tipo === 'hora' ? 'time' : 'datetime-local'} sensivel={!!sensivelPorNome(def.nome) || def.sensivel === 'oculto' || (ehExtra(def.nome) && !!sensivelDoExtra(def.nome, defDoExtra(def)))} disabled={def.desabilitado} value={(valor as string) ?? ''} onChange={(e) => aoMudar(e.target.value)} />
      );
      break;
    default: {
      const tipos: Partial<Record<TipoCampo, string>> = { email: 'email', telefone: 'tel', senha: 'password', cor: 'color' };
      // Modo discreto: e-mail, telefone, documento, endereço, conta e Pix ficam mascarados sem foco.
      const mascara = mascaraDoCampo(def);
      // Item 4 da lista B de 30/10: telefone e WhatsApp com a máscara enquanto digita, cursor no lugar e o exemplo no campo vazio.
      const entrada = def.tipo === 'telefone' ? (
        <EntradaComMascara
          mascara={mascara}
          formato={def.transformar ?? mascaraTelefoneBR}
          valor={(valor as string) ?? ''}
          aoMudarTexto={aoMudar}
          id={id}
          {...aria}
          type="tel"
          inputMode="tel"
          disabled={def.desabilitado}
          placeholder={def.placeholder ?? EXEMPLOS_MASCARA.telefone}
        />
      ) : (
        <EntradaSensivel
          mascara={mascara}
          className={def.prefixo ? 'pl-7' : undefined}
          // Texto livre (r10 visual M2 e B5): sem foco, padrões e nomes conhecidos censurados; senha e cor ficam de fora.
          livre={def.tipo === 'texto'}
          nomesNoTexto={def.nomes}
          id={id}
          {...aria}
          type={tipos[def.tipo] ?? 'text'}
          autoComplete={def.tipo === 'senha' ? 'new-password' : undefined}
          disabled={def.desabilitado}
          placeholder={def.placeholder}
          value={(valor as string) ?? ''}
          onChange={(e) => aoMudar(def.prefixo && e.target.value.startsWith(def.prefixo) ? e.target.value.slice(def.prefixo.length) : e.target.value)}
        />
      );
      controle = def.prefixo ? (
        <div className="relative">
          <span aria-hidden data-prefixo-campo="" className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-tinta-3">
            {def.prefixo}
          </span>
          {entrada}
        </div>
      ) : (
        entrada
      );
    }
  }
  return (
    <Rotulo id={id} rotulo={def.rotulo} obrigatorio={def.obrigatorio} erro={erro} ajuda={def.ajuda}>
      {def.ladoEsquerdo ? (
        <div className="flex items-center gap-2" data-lado-esquerdo={def.nome}>
          <div className="w-36 shrink-0">{def.ladoEsquerdo}</div>
          <div className="min-w-0 flex-1">{controle}</div>
        </div>
      ) : (
        controle
      )}
    </Rotulo>
  );
}

/**
 * Fase 2 (W5): documento (CPF ou CNPJ) e CEP com máscara enquanto digita. O documento mostra a dica de validade quando
 * completo (verde válido, vermelha inválido); no modo discreto os dois viram o marcador sem foco (documento mostra parte).
 */
function EntradaMascarada({ def, id, aria, valor, aoMudar }: { def: DefCampo; id: string; aria: { 'aria-invalid': boolean; 'aria-describedby'?: string }; valor: unknown; aoMudar: (v: unknown) => void }) {
  const { t } = useTranslation();
  const doc = def.tipo === 'documento';
  const texto = valor === null || valor === undefined ? '' : String(valor);
  const exibido = doc ? mascaraDocumento(texto, def.documento) : mascaraCep(texto);
  const dica = doc ? validadeDocumento(texto, def.documento) : null;
  // Lote 19/10: documento de outro país (EIN, NIF...) com a dica pelo rótulo do tipo; Espanha tem letras (teclado de texto).
  const estrangeiro = doc && ehTipoDocumentoPais(def.documento) && def.documento !== 'cpf' && def.documento !== 'cnpj' ? def.documento : null;
  const dicaPais = estrangeiro ? validadeDocumentoPais(texto, estrangeiro) : null;
  return (
    <>
      <EntradaComMascara
        mascara={doc ? 'documento' : 'oculto'}
        id={id}
        {...aria}
        inputMode={estrangeiro && paisDoTipo(estrangeiro) === 'ES' ? 'text' : 'numeric'}
        autoComplete="off"
        className="numero"
        disabled={def.desabilitado}
        placeholder={def.placeholder ?? (estrangeiro ? EXEMPLO_DOCUMENTO[estrangeiro] : doc ? (def.documento === 'cnpj' ? EXEMPLOS_MASCARA.cnpj : EXEMPLOS_MASCARA.cpfCnpj) : EXEMPLOS_MASCARA.cep)}
        formato={(v) => (doc ? mascaraDocumento(v, def.documento) : mascaraCep(v))}
        valor={exibido}
        aoMudarTexto={aoMudar}
      />
      {dica && (
        <p data-dica-documento={dica} aria-live="polite" className={`text-xs ${dica.endsWith('Valido') ? 'text-verde' : 'text-perigo'}`}>
          {t(`documento.${dica}`)}
        </p>
      )}
      {estrangeiro && dicaPais && (
        <p data-dica-documento={`${estrangeiro}${dicaPais === 'valido' ? 'Valido' : 'Invalido'}`} aria-live="polite" className={`text-xs ${dicaPais === 'valido' ? 'text-verde' : 'text-perigo'}`}>
          {t(dicaPais === 'valido' ? 'documento.paisValido' : 'documento.paisInvalido', { tipo: t(`documento.tipos.${estrangeiro}`) })}
        </p>
      )}
    </>
  );
}

/** Tipos de campo personalizado do servidor (fase 2, W5: telefone, documento, email, cep, dinheiro e texto_longo). */
type TipoCampoExtra = 'texto' | 'numero' | 'data' | 'lista' | 'booleano' | 'telefone' | 'documento' | 'email' | 'cep' | 'dinheiro' | 'texto_longo';

interface CampoExtra {
  chave: string;
  rotulo: string;
  tipo: TipoCampoExtra;
  opcoes: string[];
  obrigatorio: boolean;
  /** Marca "sensível" da definição (review r15 spec M1); servidor antigo não manda (falso). */
  sensivel?: boolean;
  /** Fase 2 (W5): texto de ajuda e condição de exibição. */
  ajuda?: string | null;
  visivel_quando?: { campo: string; valor: string } | null;
}

/** Tipo do controle de formulário para cada tipo de campo personalizado. */
const CONTROLE_DO_EXTRA: Record<TipoCampoExtra, TipoCampo> = {
  texto: 'texto',
  numero: 'numero',
  data: 'data',
  lista: 'selecao',
  booleano: 'booleano',
  telefone: 'telefone',
  documento: 'documento',
  email: 'email',
  cep: 'cep',
  dinheiro: 'dinheiro',
  texto_longo: 'area',
};

/**
 * Condição "mostrar quando" (fase 2, W5): o campo aparece quando o campo da condição (outro campo personalizado, por
 * `extras.<chave>`, ou um campo fixo do formulário, como `tipo`) tem o valor dado, comparado como texto ("true" para sim).
 */
export function visivelPelaCondicao(cond: { campo: string; valor: string } | null | undefined, chavesExtras: ReadonlySet<string>): DefCampo['visivel'] {
  if (!cond?.campo) return undefined;
  const nome = chavesExtras.has(cond.campo) ? `extras.${cond.campo}` : cond.campo;
  return (valores) => String(ler(valores, nome) ?? '') === cond.valor;
}

/** Definições de campos personalizados de uma entidade (N8) como DefCampo. */
export function useCamposPersonalizados(entidade?: string) {
  const q = useQuery({ queryKey: ['campos', entidade], queryFn: () => get<CampoExtra[]>(`/campos/${entidade}`), enabled: !!entidade, staleTime: 60_000 });
  return useMemo<DefCampo[]>(() => {
    const chaves = new Set((q.data ?? []).map((c) => c.chave));
    return (q.data ?? []).map((c) => ({
      nome: `extras.${c.chave}`,
      rotulo: c.rotulo,
      obrigatorio: c.obrigatorio,
      tipo: CONTROLE_DO_EXTRA[c.tipo] ?? 'texto',
      tipoExtra: c.tipo,
      opcoes: c.opcoes.map((o) => ({ valor: o, rotulo: o })),
      largura: c.tipo === 'texto_longo' ? 'inteira' : 'meia',
      sensivelNaDefinicao: !!c.sensivel,
      ...(c.ajuda ? { ajuda: c.ajuda } : {}),
      ...(c.visivel_quando ? { visivel: visivelPelaCondicao(c.visivel_quando, chaves) } : {}),
    }));
  }, [q.data]);
}

/**
 * Definições dos campos personalizados de uma entidade no formato da regra efetiva, por chave (sem o `extras.`), para a
 * Auditoria e o histórico do chatter (`censurarCampo`, `jsonCensurado`). Sem entidade, mapa vazio (a regra usa o valor).
 */
export function useDefsExtras(entidade?: string): ReadonlyMap<string, DefExtraSensivel> {
  const defs = useCamposPersonalizados(entidade);
  return useMemo(() => new Map(defs.map((d) => [d.nome.slice(7), defDoExtra(d)])), [defs]);
}

/**
 * Regra do campo personalizado no modo discreto (review r13 visual M2; r14: `sensivelPorNome`, a mesma do Editar e da
 * Auditoria; r15 spec M1: a regra efetiva `sensivelDoExtra`, com o tipo e a marca da definição): número e data somem sempre;
 * nascimento, partes do endereço, agência, dinheiro e o texto ou a lista com a marca `sensivel` somem; documento, conta, Pix,
 * e-mail e telefone passam pelo formatador do tipo; nome de pessoa, pelas iniciais com a opção de nomes. Null quando nada
 * disso vale (regra do tipo, texto livre censurado).
 */
function extraSensivel(chave: string, def: DefExtraSensivel | null, v: unknown): string | null {
  if (!discretoAtivo() || !chave || typeof v === 'boolean' || (typeof v === 'object' && !Array.isArray(v))) return null;
  const tipo = sensivelDoExtra(chave, def, v);
  if (!tipo) return null;
  if (tipo === 'oculto' || ehOcultoPorNome(tipo)) return OCULTO;
  const s = Array.isArray(v) ? v.map(String).join(', ') : String(v);
  if (ehDocumentoPorNome(tipo)) return documentoFormatado(s);
  if (tipo === 'pix') return chavePix(s);
  if (tipo === 'conta') return numeroConta(s);
  if (tipo === 'email') return email(s);
  if (tipo === 'telefone') return telefoneFormatado(s);
  // Nome de pessoa: iniciais com a opção de nomes; sem ela, segue a regra do tipo (texto livre censurado).
  return nomesOcultos() ? nomePessoa(s) : null;
}

/**
 * Valor de campo personalizado para leitura (review r12 visual M1), o formatador único das fichas, da Tabela e do agendamento:
 * sim ou não no idioma, data por `data()` (nunca ISO), número por `numero()` e texto (e opção de lista) por `textoLivre()`,
 * que censura padrões no modo discreto e troca os `nomes` conhecidos pelas iniciais. Sem definição (campo removido), data ISO
 * ainda vira data e o resto é texto livre. Vazio volta ''. Review r13 visual M2: no modo discreto, o nome do campo (`def.nome`,
 * `extras.<chave>`) aplica antes a regra dos campos sensíveis (`extraSensivel`; r15: a regra efetiva com o tipo e a marca).
 */
type DefLeitura = Pick<DefCampo, 'tipo'> & Partial<Pick<DefCampo, 'rotulo' | 'sensivelNaDefinicao' | 'tipoExtra'>>;

export function valorExtra(def: (DefLeitura & { nome?: string }) | null | undefined, v: unknown, ...nomes: (string | null | undefined)[]): string {
  return valorExtraDaChave(def, def?.nome ?? '', v, nomes);
}

function valorExtraDaChave(def: DefLeitura | null | undefined, chave: string, v: unknown, nomes: (string | null | undefined)[]): string {
  if (v === undefined || v === null || v === '') return '';
  const sensivel = extraSensivel(chave.replace(/^extras\./, ''), def ? defDoExtra(def) : null, v);
  if (sensivel !== null) return sensivel;
  // Fase 2 (W5, L10): tipos novos pelo formatador do tipo (o modo discreto já esconderia acima).
  const tx = def?.tipoExtra ?? def?.tipo;
  if (tx === 'dinheiro' && typeof v === 'number') return dinheiro(v);
  if (tx === 'documento') return documentoFormatado(String(v));
  if (tx === 'telefone') return telefoneFormatado(String(v));
  if (tx === 'email') return email(String(v));
  if (tx === 'cep') return String(v);
  if (def?.tipo === 'booleano' || typeof v === 'boolean') return v ? i18n.t('comum.sim') : i18n.t('comum.nao');
  if (def?.tipo === 'data' || (!def && typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v))) return data(String(v));
  if (def?.tipo === 'numero' || typeof v === 'number') {
    const n = typeof v === 'number' ? v : Number(v);
    if (Number.isFinite(n)) return numero(n, Number.isInteger(n) ? 0 : 2);
  }
  if (Array.isArray(v)) return v.map((x) => textoLivre(String(x), ...nomes)).join(', ');
  if (typeof v === 'object') return '';
  return textoLivre(String(v), ...nomes);
}

/**
 * Linhas de leitura dos campos personalizados de um registro (review r12 visual M1): rótulo da definição, na ordem das
 * definições, só os preenchidos; chave sem definição (campo removido) vem depois, com o rótulo humanizado.
 */
export function linhasExtras(defs: DefCampo[], extras: Record<string, unknown> | null | undefined, ...nomes: (string | null | undefined)[]) {
  const ex = extras ?? {};
  const conhecidas = new Set(defs.map((d) => d.nome.slice(7)));
  const linhas = defs.map((d) => ({ chave: d.nome.slice(7), rotulo: d.rotulo, valor: valorExtra(d, ex[d.nome.slice(7)], ...nomes) }));
  for (const [k, v] of Object.entries(ex)) if (!conhecidas.has(k)) linhas.push({ chave: k, rotulo: rotuloCampo(k), valor: valorExtraDaChave(null, k, v, nomes) });
  return linhas.filter((l) => l.valor !== '');
}

export function ler(valores: Record<string, unknown>, nome: string): unknown {
  if (!nome.startsWith('extras.')) return valores[nome];
  return (valores.extras as Record<string, unknown> | undefined)?.[nome.slice(7)];
}

export function escrever(valores: Record<string, unknown>, nome: string, v: unknown): Record<string, unknown> {
  if (!nome.startsWith('extras.')) return { ...valores, [nome]: v };
  return { ...valores, extras: { ...((valores.extras as Record<string, unknown>) ?? {}), [nome.slice(7)]: v } };
}

/** Validação local de obrigatórios e e-mail, a mesma do Formulario, para formulários que guardam os valores fora dele. */
export function validarCampos(campos: DefCampo[], valores: Record<string, unknown>): Record<string, string> {
  const locais: Record<string, string> = {};
  for (const c of campos) {
    if (c.visivel && !c.visivel(valores)) continue;
    const v = ler(valores, c.nome);
    const vazio = v === undefined || v === null || v === '' || (Array.isArray(v) && !v.length);
    if (c.obrigatorio && vazio && c.tipo !== 'booleano') locais[c.nome] = c.rotulo ? i18n.t('erros.obrigatorioCampo', { campo: c.rotulo }) : i18n.t('erros.obrigatorio');
    else if (c.tipo === 'email' && !vazio && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(v))) locais[c.nome] = i18n.t('erros.emailInvalido');
    else if (c.validar && !vazio) {
      const msg = c.validar(v);
      if (msg) locais[c.nome] = msg;
    }
  }
  return locais;
}

/** Campos de data com texto inválido (31/02/2026) que o valor do formulário não enxerga: nome do campo para a mensagem. */
export function datasInvalidas(form: HTMLElement): Record<string, string> {
  const erros: Record<string, string> = {};
  for (const el of form.querySelectorAll<HTMLElement>('input[data-data-invalida]')) {
    const nome = el.closest<HTMLElement>('[data-campo]')?.dataset.campo;
    if (nome) erros[nome] = i18n.t('comum.dataInvalida');
  }
  return erros;
}

/**
 * Leva o foco ao primeiro controle de campo com erro, na ordem da tela. Review r13 visual M1: o campo sensível sem foco é o
 * `MarcadorSensivel` (sem input); o seletor inclui `[data-marcador]`, e focar o marcador abre o campo real com o cursor no fim.
 * O `Formulario` chama esta função depois do render dos erros (o campo com `aria-invalid` já é o input real).
 */
export function focarPrimeiroInvalido(form: HTMLElement, nomes: string[]) {
  const alvo = [...form.querySelectorAll<HTMLElement>('[data-campo]')].find((c) => nomes.includes(c.dataset.campo ?? ''));
  alvo?.querySelector<HTMLElement>('input:not([aria-hidden]), select, textarea, button[role="switch"], [data-marcador]')?.focus();
}

const LARGURAS = { inteira: 'sm:col-span-6', meia: 'sm:col-span-3', terco: 'sm:col-span-2' };
/**
 * Lote 19 (item 21, ritmo do design system): o título do grupo abre uma seção, com divisória e respiro acima (o primeiro do
 * formulário fica sem a divisória), em vez de colado no último campo do grupo anterior.
 */
/** A divisória de seção do formulário, também para bloco sem título de grupo (a pergunta das filiais). */
export const LINHA_DE_GRUPO = 'mt-2 border-t border-borda pt-5';
const TITULO_GRUPO = `kicker col-span-full ${LINHA_DE_GRUPO} first:mt-0 first:border-t-0 first:pt-0`;

/** Campos visíveis em sequência, agrupados pelo `grupo` (C4 e C5: título, conteúdo e recolhível por grupo). */
function segmentos(vis: DefCampo[]): { grupo?: string; itens: DefCampo[] }[] {
  const saida: { grupo?: string; itens: DefCampo[] }[] = [];
  for (const c of vis) {
    const ultimo = saida.at(-1);
    if (ultimo && ultimo.grupo === c.grupo) ultimo.itens.push(c);
    else saida.push({ grupo: c.grupo, itens: [c] });
  }
  return saida;
}

/**
 * C5: os campos personalizados com lugar entram logo depois da ÚLTIMA definição do campo fixo pedido (o mesmo nome pode
 * ter várias, uma por condição, como o documento) e herdam o grupo dele.
 */
export function comExtrasPosicionados(campos: DefCampo[], extras: DefCampo[], posicao: Record<string, { depoisDe: string; largura?: DefCampo['largura'] }>): DefCampo[] {
  const ultimoIndice = new Map<string, number>();
  campos.forEach((c, k) => ultimoIndice.set(c.nome, k));
  const saida: DefCampo[] = [];
  campos.forEach((c, k) => {
    saida.push(c);
    if (ultimoIndice.get(c.nome) !== k) return;
    for (const e of extras) {
      const pos = posicao[e.nome.slice(7)];
      // `largura` na posição (VIS-B2): só quem pede muda; os demais formulários seguem o padrão do campo personalizado.
      if (pos?.depoisDe === c.nome) saida.push({ ...e, grupo: c.grupo, ...(pos.largura ? { largura: pos.largura } : {}) });
    }
  });
  // Campo fixo pedido que não está no formulário: o personalizado fica no fim da lista principal, sem perder o lugar.
  for (const e of extras) if (!saida.some((x) => x.nome === e.nome)) saida.push(e);
  return saida;
}

/**
 * Formulário guiado por definição de campos, com validação local, erros do servidor por campo
 * e campos personalizados da entidade (F7, N8).
 */
export function Formulario({
  id,
  campos: camposBase,
  telaCampos,
  inicial,
  entidade,
  aoEnviar,
  rotulosAtuais,
  ajuste,
  sobrepor,
  sobreporChave,
  acompanhar,
  aoMudar,
  inicioDoGrupo,
  fimDoGrupo,
  gruposRecolhiveis,
  recolhiveisAoFim,
  posicaoExtras,
  extrasOcultos,
  depoisDoCampo,
  children,
}: {
  id: string;
  campos: DefCampo[];
  /**
   * Lote de 19/10 (item 9): tela de cadastro (`cadastros.parceiro`) cujos campos o workspace esconde ou torna obrigatórios
   * (Admin > Configurações > Campos das telas). Com ela, a escolha vale aqui e quem configura vê o atalho. Aditivo (não é a `tela` da Tabela, a das visões salvas).
   */
  telaCampos?: string;
  inicial?: Record<string, unknown>;
  entidade?: string;
  aoEnviar: (valores: Record<string, unknown>) => Promise<unknown>;
  rotulosAtuais?: Record<string, string>;
  /**
   * Fase 2 (W5): dados vindos de fora do formulário (consulta de CNPJ pelo botão) mesclados SÓ nos campos vazios quando `n`
   * muda; o que a pessoa já digitou fica.
   */
  ajuste?: { n: number; dados: Record<string, unknown> };
  /**
   * Review r47 visual (M1): valores de fora que mandam no campo (o valor da oportunidade com itens é a soma deles, e o campo
   * fica travado). Quando mudam, sobrescrevem só esses campos; o resto do que a pessoa digitou fica. Aditivo.
   */
  sobrepor?: Record<string, unknown>;
  /** Reaplica `sobrepor` quando esta chave muda, mesmo com os mesmos valores (a troca entre dois países sem moeda própria). */
  sobreporChave?: string;
  /**
   * Lista de 04/11, item 3: sugestão de fora que acompanha outros campos (a moeda segue a vertical e o cliente). Ao contrário de
   * `sobrepor`, só a entrada que mudou é escrita: o resto do que está na mesma lista não volta. Aditivo.
   */
  acompanhar?: Record<string, unknown>;
  /** Fase 2 (W12): avisa a tela a cada mudança (totais e prévia ao vivo fora dos campos). */
  aoMudar?: (valores: Record<string, unknown>) => void;
  /**
   * C4 (lote de 09/10): conteúdo da tela dentro de um grupo (pelo título do `grupo`), logo depois do título ou depois do
   * último campo visível dele. Os itens do contrato ficam ENTRE as áreas, como no antigo. Aditivo: sem a prop, nada muda.
   */
  inicioDoGrupo?: Record<string, ReactNode>;
  fimDoGrupo?: Record<string, ReactNode>;
  /** C5 (lote de 09/10): grupos (pelo título) que abrem e fecham, fechados de início (os dados fiscais da PJ na criação). */
  gruposRecolhiveis?: string[];
  /** U15 F5: os grupos recolhíveis ("Mais opções" do modo simples) saem depois do `children` (itens, anexo), no fim do formulário. */
  recolhiveisAoFim?: boolean;
  /**
   * C5: lugar de campos personalizados (pela chave, sem `extras.`): logo depois do campo fixo `depoisDe` e no grupo dele (o
   * WhatsApp no Contato, o RG junto do CPF). O campo que não existe na definição do workspace simplesmente não aparece.
   */
  posicaoExtras?: Record<string, { depoisDe: string; prefixo?: string; largura?: DefCampo['largura'] }>;
  /**
   * Lista de 02/11 (CRM, item 1): campos personalizados (pela chave, sem `extras.`) que não aparecem neste formulário (o WhatsApp
   * do CRM tem campo próprio). O valor guardado NÃO se perde: `extras` segue indo inteiro na gravação, como veio do registro.
   * Definição OBRIGATÓRIA não se oculta (rl0211-2 ROB-B5): o servidor recusaria o cadastro (`campo.obrigatorio`) por um campo escondido.
   */
  extrasOcultos?: string[];
  /**
   * Ajustes do CRM de 27/10: conteúdo da tela logo depois de um campo (pelo nome), na largura inteira (os telefones e e-mails
   * adicionais do contato, os segmentos da empresa). Aditivo: sem a prop, nada muda.
   */
  depoisDoCampo?: Record<string, ReactNode>;
  children?: ReactNode;
}) {
  const extrasTodos = useCamposPersonalizados(entidade);
  const extrasBase = useMemo(() => (extrasOcultos?.length ? extrasTodos.filter((e) => e.obrigatorio || !extrasOcultos.includes(e.nome.slice(7))) : extrasTodos), [extrasTodos, extrasOcultos?.join('|')]); // eslint-disable-line react-hooks/exhaustive-deps
  const extras = useMemo(() => (posicaoExtras ? extrasBase.map((e) => (posicaoExtras[e.nome.slice(7)]?.prefixo ? { ...e, prefixo: posicaoExtras[e.nome.slice(7)]!.prefixo } : e)) : extrasBase), [extrasBase]); // eslint-disable-line react-hooks/exhaustive-deps
  const campos = useCamposDaTela(telaCampos).aplicar(camposBase);
  const [valores, setValores] = useState<Record<string, unknown>>(inicial ?? {});
  useEffect(() => {
    if (ajuste?.n) setValores((x) => mesclarVazios(x, ajuste.dados));
  }, [ajuste?.n]); // eslint-disable-line react-hooks/exhaustive-deps
  const chaveSobrepor = sobrepor ? (sobreporChave ?? JSON.stringify(sobrepor)) : '';
  useEffect(() => {
    if (!sobrepor) return;
    setValores((x) => Object.entries(sobrepor).reduce((acc, [nome, v]) => escrever(acc, nome, v), x));
    // RCRM5 SPEC-B3: o valor posto de fora (o lead criado na hora) também move a sugestão que segue o campo.
    for (const c of camposBase) if (c.preencherSugere && c.nome in sobrepor) consultar(c, prontoParaConsulta(c, sobrepor[c.nome]));
  }, [chaveSobrepor]); // eslint-disable-line react-hooks/exhaustive-deps
  const acompanhados = useRef<Record<string, unknown>>({});
  const chaveAcompanhar = acompanhar ? JSON.stringify(acompanhar) : '';
  useEffect(() => {
    if (!acompanhar) return;
    const antes = acompanhados.current;
    acompanhados.current = acompanhar;
    setValores((x) => Object.entries(acompanhar).reduce((acc, [nome, v]) => (Object.is(antes[nome], v) ? acc : escrever(acc, nome, v)), x));
  }, [chaveAcompanhar]); // eslint-disable-line react-hooks/exhaustive-deps
  // Fase 2 (W5): última consulta pedida por campo (`preencher`), para não repetir a mesma.
  const consultas = useRef(new Map<string, string>());
  // RCRM5 SPEC-B3: o que cada consulta que sugere (`preencherSugere`) preencheu por último, por campo de origem.
  const sugeridos = useRef(new Map<string, Record<string, unknown>>());
  function consultar(c: DefCampo, pronto: string | null) {
    if (!c.preencher) return;
    if (!pronto) {
      // Sugestão sem origem (a empresa saiu do campo): o que ela preencheu e ninguém mudou volta a vazio.
      if (c.preencherSugere && sugeridos.current.get(c.nome)) {
        consultas.current.delete(c.nome);
        const antes = sugeridos.current.get(c.nome) ?? {};
        sugeridos.current.delete(c.nome);
        setValores((x) => mesclarSugestao(x, null, antes).valores);
      }
      return;
    }
    if (consultas.current.get(c.nome) === pronto) return;
    consultas.current.set(c.nome, pronto);
    void c
      .preencher(pronto)
      .then((dados) => {
        // ROB-B4: só vale a resposta do valor atual do campo (a escolha mais recente).
        if (consultas.current.get(c.nome) !== pronto) return;
        if (!c.preencherSugere) {
          if (dados) setValores((x) => mesclarVazios(x, dados));
          return;
        }
        const antes = sugeridos.current.get(c.nome) ?? {};
        setValores((x) => {
          const m = mesclarSugestao(x, dados, antes);
          sugeridos.current.set(c.nome, m.preenchidos);
          return m.valores;
        });
      })
      .catch(() => undefined);
  }
  // SPEC-B3: com o campo de origem já preenchido na abertura (a empresa da ficha), a sugestão é buscada no início.
  useEffect(() => {
    for (const c of camposBase) if (c.preencherSugere && c.preencher) consultar(c, prontoParaConsulta(c, ler(valores, c.nome)));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  function mudar(c: DefCampo, bruto: unknown) {
    // Lote 19/10 (item 23): telefone sem máscara própria ganha a do núcleo enquanto digita (cadastro, campo personalizado).
    const transformar = c.transformar ?? (c.tipo === 'telefone' ? mascaraTelefoneBR : undefined);
    const v = transformar && typeof bruto === 'string' ? transformar(bruto) : bruto;
    // Fase 2 (W19): trocar este campo limpa os dependentes (o tipo da chave Pix limpa a chave).
    setValores((x) => (c.limpa ?? []).reduce((acc, nome) => escrever(acc, nome, null), escrever(x, c.nome, v)));
    consultar(c, prontoParaConsulta(c, v));
  }
  const [erros, setErros] = useState<Record<string, string>>({});
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const todos = [...campos, ...extras];
  // C5: campos personalizados com lugar próprio entram depois do campo fixo pedido (no grupo dele); os outros ficam no fim.
  const posicionados = posicaoExtras ? extras.filter((e) => posicaoExtras[e.nome.slice(7)]) : [];
  const lista = posicionados.length ? comExtrasPosicionados(campos, posicionados, posicaoExtras!) : campos;
  const restantes = posicionados.length ? extras.filter((e) => !posicionados.includes(e)) : extras;
  const [abertos, setAbertos] = useState<Record<string, boolean>>({});
  const formRef = useRef<HTMLFormElement>(null);
  const alertaRef = useRef<HTMLParagraphElement>(null);
  // Depois de uma resposta de erro do servidor o foco vai para a mensagem geral ou para o primeiro campo inválido (o botão pode ter ficado ocupado e o foco, solto).
  const [focoErro, setFocoErro] = useState(0);
  useEffect(() => {
    if (!focoErro) return;
    if (alertaRef.current) alertaRef.current.focus();
    else if (formRef.current) focarPrimeiroInvalido(formRef.current, Object.keys(erros).flatMap((k) => [k, `extras.${k}`]));
  }, [focoErro]); // eslint-disable-line react-hooks/exhaustive-deps
  // Erro de validação local (review r13 visual M1): o foco vai ao primeiro campo inválido DEPOIS do render dos erros, quando o
  // campo com `aria-invalid` já trocou o marcador do modo discreto pelo input real.
  const [focoLocal, setFocoLocal] = useState<{ n: number; nomes: string[] }>({ n: 0, nomes: [] });
  useEffect(() => {
    if (focoLocal.n && formRef.current) focarPrimeiroInvalido(formRef.current, focoLocal.nomes);
  }, [focoLocal]);
  useEffect(() => {
    aoMudar?.(valores);
  }, [valores]); // eslint-disable-line react-hooks/exhaustive-deps

  // rl0311-2 ROB2-M1: duplo clique ou Enter repetido não mandam de novo enquanto o primeiro envio não voltou.
  const enviando = useRef(false);
  async function enviar(e: FormEvent) {
    e.preventDefault();
    if (enviando.current) return;
    const form = e.currentTarget as HTMLFormElement;
    const locais = { ...validarCampos(todos, valores), ...datasInvalidas(form) };
    setErros(locais);
    setErroGeral(null);
    if (Object.keys(locais).length) {
      setFocoLocal((x) => ({ n: x.n + 1, nomes: Object.keys(locais) }));
      return;
    }
    enviando.current = true;
    try {
      await aoEnviar(valores);
    } catch (err) {
      if (err instanceof ErroRequisicao) {
        const porCampo = err.porCampo();
        setErros(porCampo);
        if (!Object.keys(porCampo).length || !todos.some((c) => porCampo[c.nome] ?? porCampo[c.nome.slice(7)])) setErroGeral(mensagemDeErro(err));
      } else setErroGeral(mensagemDeErro(err));
      setFocoErro((n) => n + 1);
    } finally {
      enviando.current = false;
    }
  }

  // U15 F5: `aoFim` separa os grupos recolhíveis (saem depois do `children` quando `recolhiveisAoFim`).
  const renderSegs = (aoFim: boolean) =>
    segmentos(lista.filter((c) => !c.visivel || c.visivel(valores)))
      .filter((seg) => !recolhiveisAoFim || (!!seg.grupo && !!gruposRecolhiveis?.includes(seg.grupo)) === aoFim)
      .map((seg, k) => {
          const corpo = (
            <>
              {seg.grupo && inicioDoGrupo?.[seg.grupo]}
              {seg.itens.map((c) => (
                <Fragment key={c.nome}>
                  <div data-campo={c.nome} className={LARGURAS[c.largura ?? 'inteira']}>
                    <CampoControlado
                      def={c}
                      valor={ler(valores, c.nome)}
                      erro={erros[c.nome] ?? (c.nome.startsWith('extras.') ? erros[c.nome.slice(7)] : undefined)}
                      rotuloAtual={rotulosAtuais?.[c.nome]}
                      aoMudar={(v) => mudar(c, v)}
                    />
                  </div>
                  {depoisDoCampo?.[c.nome] && (
                    <div className="sm:col-span-6" data-depois-do-campo={c.nome}>
                      {depoisDoCampo[c.nome]}
                    </div>
                  )}
                </Fragment>
              ))}
              {seg.grupo && fimDoGrupo?.[seg.grupo]}
            </>
          );
          const g = seg.grupo;
          if (g && gruposRecolhiveis?.includes(g)) {
            // C5: grupo recolhível (o mesmo `details` de Minha conta e da Importação); abre sozinho com erro num campo dele.
            const comErro = seg.itens.some((c) => erros[c.nome] ?? (c.nome.startsWith('extras.') ? erros[c.nome.slice(7)] : undefined));
            const aberto = !!abertos[g] || comErro;
            return (
              <details
                key={`g:${g}`}
                data-grupo-recolhivel={g}
                open={aberto}
                onToggle={(e) => {
                  const agora = (e.currentTarget as HTMLDetailsElement).open;
                  if (agora !== aberto) setAbertos((x) => ({ ...x, [g]: agora }));
                }}
                className="min-w-0 rounded-lg border border-borda sm:col-span-6"
              >
                <summary className="cursor-pointer px-3 py-3 text-sm font-medium text-tinta-1 md:py-2">{g}</summary>
                <div className="grid grid-cols-1 gap-4 border-t border-borda px-3 py-3 sm:grid-cols-6">{corpo}</div>
              </details>
            );
          }
          return (
            <Fragment key={g ? `g:${g}` : `s:${k}`}>
              {g && <p className={TITULO_GRUPO}>{g}</p>}
              {corpo}
            </Fragment>
          );

      });

  return (
    <form ref={formRef} id={id} data-datas-proprias onSubmit={enviar} noValidate className="grid grid-cols-1 gap-4 sm:grid-cols-6">
      {erroGeral && (
        <p ref={alertaRef} tabIndex={-1} role="alert" className="rounded-lg border border-perigo px-3 py-2 text-sm text-perigo col-span-full focus:outline-2 focus:outline-foco">
          {erroGeral}
        </p>
      )}
      {telaCampos && <AtalhoCamposDaTela telaCampos={telaCampos} />}
      {renderSegs(false)}
      {/* Lista de 30/10 (B), item 33: os campos personalizados sem lugar próprio entram no fim, junto dos demais, sem área separada. */}
      {restantes
        .filter((c) => !c.visivel || c.visivel(valores))
        .map((c) => (
          <div key={c.nome} data-campo={c.nome} className={LARGURAS[c.largura ?? 'meia']}>
            <CampoControlado def={c} valor={ler(valores, c.nome)} erro={erros[c.nome] ?? erros[c.nome.slice(7)]} aoMudar={(v) => setValores((x) => escrever(x, c.nome, v))} />
          </div>
        ))}
      {children}
      {recolhiveisAoFim && renderSegs(true)}
    </form>
  );
}
